import {
  ScoutingReport,
  PerformanceReport,
  MedicalReport,
  MedicalReportRevision,
  TrainingReport,
  ApiResponse,
  ScoutingReportForm,
  PerformanceReportForm,
  MedicalReportForm,
  MedicalReportRevisionForm,
  TrainingReportForm,
} from '@/types';
import api from './client';

// The Go backend parses report date fields as RFC3339 timestamps, but the
// <input type="date"> controls produce bare YYYY-MM-DD strings (e.g.
// "2026-05-31"). Normalise any date-only value to a full RFC3339 timestamp at
// midnight UTC so the backend's time.Parse succeeds.
const toRFC3339Date = (value: unknown): unknown =>
  typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? `${value}T00:00:00Z`
    : value;

// Scouting Reports API
export const scoutingReportsAPI = {
  getById: async (id: string): Promise<ApiResponse<ScoutingReport>> => {
    const response = await api.get(`/scouting-reports/${id}`);
    return response.data;
  },
  getByPlayer: async (playerId: string): Promise<ApiResponse<ScoutingReport[]>> => {
    const response = await api.get(`/players/${playerId}/scouting-reports`);
    return response.data;
  },

  create: async (report: ScoutingReportForm): Promise<ApiResponse<ScoutingReport>> => {
    const payload: Record<string, unknown> = { ...report, player_id: report.playerId };
    delete payload.playerId;
    if (report.reportDate !== undefined) payload.report_date = toRFC3339Date(report.reportDate);
    delete payload.reportDate;
    const response = await api.post('/scouting-reports', payload);
    return response.data;
  },

  update: async (id: string, report: Partial<ScoutingReportForm>): Promise<ApiResponse<ScoutingReport>> => {
    const payload: Record<string, unknown> = { ...report };
    if (payload.playerId !== undefined) {
      payload.player_id = payload.playerId;
      delete payload.playerId;
    }
    if (payload.reportDate !== undefined) {
      payload.report_date = toRFC3339Date(payload.reportDate);
      delete payload.reportDate;
    }
    const response = await api.put(`/scouting-reports/${id}`, payload);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/scouting-reports/${id}`);
  },
};

// Performance Reports API
export const performanceReportsAPI = {
  getById: async (id: string): Promise<ApiResponse<PerformanceReport>> => {
    const response = await api.get(`/performance-reports/${id}`);
    return response.data;
  },
  getByPlayer: async (playerId: string): Promise<ApiResponse<PerformanceReport[]>> => {
    const response = await api.get(`/players/${playerId}/performance-reports`);
    return response.data;
  },

  create: async (report: PerformanceReportForm): Promise<ApiResponse<PerformanceReport>> => {
    const payload: Record<string, unknown> = { ...report };
    payload.player_id = report.playerId;
    payload.report_type = report.reportType;
    if (report.matchDate !== undefined) payload.match_date = toRFC3339Date(report.matchDate);
    if (report.minutesPlayed !== undefined) payload.minutes_played = report.minutesPlayed;
    if (report.distanceCovered !== undefined) payload.distance_covered = report.distanceCovered;
    if (report.goals !== undefined) payload.goals = report.goals;
    if (report.assists !== undefined) payload.assists = report.assists;
    payload.rating = report.rating;
    delete payload.playerId;
    delete payload.reportType;
    delete payload.matchDate;
    delete payload.minutesPlayed;
    delete payload.distanceCovered;
    const response = await api.post('/performance-reports', payload);
    return response.data;
  },

  update: async (id: string, report: Partial<PerformanceReportForm>): Promise<ApiResponse<PerformanceReport>> => {
    const payload: Record<string, unknown> = { ...report };
    if (payload.playerId !== undefined) {
      payload.player_id = payload.playerId;
      delete payload.playerId;
    }
    if (payload.reportType !== undefined) {
      payload.report_type = payload.reportType;
      delete payload.reportType;
    }
    if (payload.matchDate !== undefined) {
      payload.match_date = toRFC3339Date(payload.matchDate);
      delete payload.matchDate;
    }
    if (payload.minutesPlayed !== undefined) {
      payload.minutes_played = payload.minutesPlayed;
      delete payload.minutesPlayed;
    }
    if (payload.distanceCovered !== undefined) {
      payload.distance_covered = payload.distanceCovered;
      delete payload.distanceCovered;
    }
    const response = await api.put(`/performance-reports/${id}`, payload);
    return response.data;
  },
};

// Medical Reports API
export const medicalReportsAPI = {
  getById: async (id: string): Promise<ApiResponse<MedicalReport>> => {
    const response = await api.get(`/medical-reports/${id}`);
    return response.data;
  },
  getByPlayer: async (playerId: string): Promise<ApiResponse<MedicalReport[]>> => {
    const response = await api.get(`/players/${playerId}/medical-reports`);
    return response.data;
  },

  create: async (report: MedicalReportForm): Promise<ApiResponse<MedicalReport>> => {
    const payload: Record<string, unknown> = { ...report };
    payload.player_id = report.playerId;
    if (report.injuryType !== undefined) payload.injury_type = report.injuryType;
    payload.report_date = toRFC3339Date(report.reportDate);
    delete payload.playerId;
    delete payload.injuryType;
    delete payload.reportDate;
    const response = await api.post('/medical-reports', payload);
    return response.data;
  },

  update: async (id: string, report: Partial<MedicalReportForm>): Promise<ApiResponse<MedicalReport>> => {
    const payload: Record<string, unknown> = { ...report };
    if (payload.playerId !== undefined) {
      payload.player_id = payload.playerId;
      delete payload.playerId;
    }
    if (payload.injuryType !== undefined) {
      payload.injury_type = payload.injuryType;
      delete payload.injuryType;
    }
    if (payload.reportDate !== undefined) {
      payload.report_date = toRFC3339Date(payload.reportDate);
      delete payload.reportDate;
    }
    const response = await api.put(`/medical-reports/${id}`, payload);
    return response.data;
  },
  // Revisions
  listRevisions: async (reportId: string): Promise<ApiResponse<MedicalReportRevision[]>> => {
    const response = await api.get(`/medical-reports/${reportId}/revisions`);
    return response.data;
  },
  createRevision: async (reportId: string, payload: MedicalReportRevisionForm): Promise<ApiResponse<MedicalReportRevision>> => {
    const response = await api.post(`/medical-reports/${reportId}/revisions`, payload);
    return response.data;
  },
};

// Training Reports API
// The training-report API uses snake_case keys. Map every camelCase field
// (not just player_id/session_date) so scores and the on-time flag actually
// bind on the server instead of silently defaulting to null/false.
function toTrainingPayload(report: Partial<TrainingReportForm>): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  if (report.playerId !== undefined) payload.player_id = report.playerId;
  if (report.sessionDate !== undefined) payload.session_date = report.sessionDate;
  if (report.attended !== undefined) payload.attended = report.attended;
  if (report.fitnessScore !== undefined) payload.fitness_score = report.fitnessScore;
  if (report.enduranceScore !== undefined) payload.endurance_score = report.enduranceScore;
  if (report.intensityScore !== undefined) payload.intensity_score = report.intensityScore;
  if (report.attitudeScore !== undefined) payload.attitude_score = report.attitudeScore;
  if (report.turnedUpOnTime !== undefined) payload.turned_up_on_time = report.turnedUpOnTime;
  if (report.notes !== undefined) payload.notes = report.notes;
  return payload;
}

export const trainingReportsAPI = {
  getById: async (id: string): Promise<ApiResponse<TrainingReport>> => {
    const response = await api.get(`/training-reports/${id}`);
    return response.data;
  },
  getByPlayer: async (playerId: string): Promise<ApiResponse<TrainingReport[]>> => {
    const response = await api.get(`/players/${playerId}/training-reports`);
    return response.data;
  },
  create: async (report: TrainingReportForm): Promise<ApiResponse<TrainingReport>> => {
    const response = await api.post('/training-reports', toTrainingPayload(report));
    return response.data;
  },
  update: async (id: string, report: Partial<TrainingReportForm>): Promise<ApiResponse<TrainingReport>> => {
    const response = await api.put(`/training-reports/${id}`, toTrainingPayload(report));
    return response.data;
  },
  delete: async (id: string): Promise<void> => {
    await api.delete(`/training-reports/${id}`);
  },
};

// Unified Reports feed API
export const reportsFeedAPI = {
  list: async (params?: {
    type?: 'scouting' | 'performance' | 'medical' | 'training';
    playerId?: string;
    createdBy?: string;
    startDate?: string; // ISO date string
    endDate?: string;   // ISO date string
    page?: number;
    limit?: number;
    sort?: 'asc' | 'desc';
    minRating?: number;
    maxRating?: number;
    medicalStatus?: 'fit' | 'injured' | 'recovering' | 'questionable';
    medicalSeverity?: 'minor' | 'moderate' | 'severe';
  }): Promise<{ data: Array<{ id: string; type: string; playerId: string; playerName?: string; authorId: string; authorName?: string; date: string; rating?: number }>; total: number; page: number; limit: number; totalPages: number }> => {
    const sp = new URLSearchParams();
    if (params?.type) sp.append('type', params.type);
    if (params?.playerId) sp.append('player_id', params.playerId);
    if (params?.createdBy) sp.append('created_by', params.createdBy);
    if (params?.startDate) sp.append('start_date', params.startDate);
    if (params?.endDate) sp.append('end_date', params.endDate);
    if (params?.page) sp.append('page', String(params.page));
    if (params?.limit) sp.append('limit', String(params.limit));
    if (params?.sort) sp.append('sort', params.sort);
    if (params?.minRating !== undefined) sp.append('min_rating', String(params.minRating));
    if (params?.maxRating !== undefined) sp.append('max_rating', String(params.maxRating));
    if (params?.medicalStatus) sp.append('medical_status', params.medicalStatus);
    if (params?.medicalSeverity) sp.append('medical_severity', params.medicalSeverity);
    const response = await api.get(`/reports?${sp.toString()}`);
    return response.data;
  },
};
